import {Draggable, gsap, InertiaPlugin} from "gsap/all";
import $ from "jquery";
import {BaseGameBBAI} from "../base/BaseGameBBAI";
import {AIControlOledAppScreenTransition} from "./AIControlOledAppScreenTransition";
import {Weather} from "./components/Weather";
import {ScrollingNumbers} from "./components/ScrollingNumbers";
import {callback} from "chart.js/helpers";

import Swiper from "swiper";
import {EffectFade, Autoplay} from "swiper/modules";
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/effect-fade';

// Constructor
export class AIControlOledApp extends BaseGameBBAI {

  constructor() {
    super(`data/ai-control-oled.json`, 'ai-control');
    gsap.registerPlugin(Draggable, InertiaPlugin);
    this.screenTransition = new AIControlOledAppScreenTransition();

    this.weather = null;
    this.currentAlgorithm = null;
    this.currentBuilding = null;
    this.currentKPIKey = null;
    this.currentKPIsModifiers = [];
    this.$container = null;

    // We skip screen 1 and go straight to screen 2 to have the slider
    this.defaultScreen = 2;
  }

  initGame() {
    super.initGame(this.defaultScreen);

    this.$container = $("#algorithms-selectors-container");
    this.weather = new Weather();

    this.navSwiper = null
    this.slider = null;

    this.initSliders();
    this.initKPIs();
    this.initEvents();

    this.setPreferredUnit();

    this.screensCount = $(".screen").length;
  }

  initKPIs() {
    this.$container.find(".kpi").each((i, el) => {
      $(el).data("scrolling-numbers", new ScrollingNumbers($(el).find(".scrolling-numbers")));
    })
  }

  initSliders() {
    const totalSlides = $('#building-swiper .swiper-slide').length;

    // Navigation Swiper
    this.navSwiper = new Swiper('#building-names-window', {
      modules: [Autoplay],
      autoplay: {
        delay: 5000,
      },
      slidesPerView: 'auto',
      centeredSlides: true,
      loop: true,
      loopedSlides: totalSlides,
    });

    // Main Swiper
    this.slider = new Swiper('#building-swiper', {
      modules: [EffectFade],
      slidesPerView: 1,
      loop: true,
      loopedSlides: totalSlides,
      effect: 'fade',
      fadeEffect: {crossFade: true},
      speed: 600,
    });

    // Match navigation/swiper positions
    this.navSwiper.on('slideChangeTransitionStart', () => {
      this.slider.slideToLoop(this.navSwiper.realIndex);
    });
  }

  playSlider() {
    // Resume autoplay from the current position
    this.navSwiper?.autoplay?.start();
  }

  pauseSlider() {
    // Pause autoplay
    this.navSwiper?.autoplay?.stop();
  }

  selectKPIs(algorithm, kpiKey, force = false) {
    if (!algorithm) return;

    const $algo = this.$container.find(`.algorithm-selector[data-algorithm='${algorithm}']`);

    if (!kpiKey) {
      kpiKey = this.getFirstEntry($algo.find(".kpi").first().data("values")).key
      force = true;
    }

    if (force || this.currentKPIKey !== kpiKey) {
      this.currentKPIKey = kpiKey;
    }

    // Now let's the the value linked to that key for each KPI and assign that value
    $algo.find(".kpi").each((i, el) => {
      const values = $(el).data("values");
      const value = values[this.currentKPIKey];
      if (!value) return;

      let newValue = this.getKPIValue(i, value);
      $(el).data("scrolling-numbers").setValue(newValue);

      let newUnit = this.getKPIUnit($(el), value);
      $(el).find(`.kpi-unit .${app.i18n.lang}`).text(newUnit);
    })
  }


  getKPIValue(i, value) {
    // Apply the modifier
    let newValue = value[app.i18n.lang] * (this.currentKPIsModifiers[i] ?? 1);

    // Now check if the original value did not have decimals and trim the decimals if so
    // console.log("Original KPI value =>", value[app.i18n.lang], "Converted =>", newValue);
    if (value[app.i18n.lang] % 1 === 0) {
      newValue = Math.round(newValue);
      // console.log("Original KPI value did not have decimals, so trim them to =>", newValue);
    }

    return newValue;
  }

  getKPIUnit($el, value) {

    let unitsModifiersMap = {
      hours: {
        singular: {
          fr: "heure",
          en: "hour"
        },
        plural: {
          fr: "heures",
          en: "hours"
        }
      }
    }

    let currentUnit = $el.find(`.kpi-unit .${app.i18n.lang}`).text();


    // Handle modifiers

    // Here we handle the word "hour" to add or remove the "s" at the end
    if (currentUnit.startsWith("hour") || currentUnit.startsWith("heure")) {
      currentUnit = value[app.i18n.lang] <= 1 ? unitsModifiersMap.hours.singular[app.i18n.lang] : unitsModifiersMap.hours.plural[app.i18n.lang];
    }

    return currentUnit;
  }

  updateBuildingVisually(algorithm, buildingType) {
    if (!algorithm) return;

    const $currentAlgo = this.$container.find(".algorithm-selector.active");
    const $newAlgo = this.$container.find(`.algorithm-selector[data-algorithm='${algorithm}']`);

    // If buildingType is not set, use the first one
    if (!buildingType) {
      buildingType = $newAlgo.find(`[data-building-type]`).first().attr("data-building-type")
    }

    const $currentBuilding = $currentAlgo.find(".building-selector-element.active");
    const $newBuilding = $newAlgo.find(`[data-building-type='${buildingType}']`);
    if (!$newBuilding.length) return;

    // If it's the same algo and building do nothing
    if (this.currentAlgorithm === algorithm && this.currentBuilding === buildingType) return;

    this.currentAlgorithm = algorithm;
    this.currentBuilding = buildingType;

    const kpisModifiers = app.data.content.algorithms[algorithm]?.buildings?.[buildingType]?.kpis_modifiers;

    if (kpisModifiers) {
      // Apply the modifiers if they exist
      this.currentKPIsModifiers = kpisModifiers;

      // Force update the KPIs
      this.selectKPIs(this.currentAlgorithm, this.currentKPIKey, true);
    }

    // Kill/reset timeline before building a new one
    if (this.currentTimeline) {
      this.currentTimeline.progress(1, false);
      this.currentTimeline.kill();
      this.currentTimeline = null;
    }
    this.currentTimeline = gsap.timeline();

    // If it's not the same algo, we need to fade out current algo
    const isSameAlgo = $currentAlgo[0] === $newAlgo[0];
    if (!isSameAlgo) {
      // Reset all buildings in incoming algo before fading it in
      gsap.set($newAlgo.find(".building-selector-element"), {autoAlpha: 0});
      $newAlgo.addClass("active");

      if ($currentAlgo.length) {
        // Crossfade algos and fade out current building simultaneously so total duration matches a building-only switch
        this.currentTimeline.to($currentAlgo, {autoAlpha: 0, duration: 0.5, ease: "power1.out"}, 0);
        this.currentTimeline.to($newAlgo, {autoAlpha: 1, duration: 0.5, ease: "power1.out"}, 0);
        this.currentTimeline.to($currentBuilding, {autoAlpha: 0, duration: 0.5, ease: "power1.out"}, 0);
      } else {
        // No current algo, just fade in the new one
        this.currentTimeline.to($newAlgo, {autoAlpha: 1, duration: 0.5, ease: "power1.out"}, 0);
      }

      $currentAlgo.removeClass("active");
      $currentBuilding.removeClass("active");
    }

    // Fade out current building before fading in the new one
    if ($currentBuilding.length && isSameAlgo) {
      this.currentTimeline.to($currentBuilding, {autoAlpha: 0, duration: 0.5, ease: "power1.out"}, 0);
      $currentBuilding.removeClass("active");
    }
    // Fade in new building, after fade out
    this.currentTimeline.to($newBuilding, {autoAlpha: 1, duration: 0.5, ease: "power1.out"});

    // Update active building class
    $newAlgo.find(".building-selector-element").removeClass("active");
    $newBuilding.addClass("active");
  }

  initEvents() {
    // Debugging trigger with key presses
    $(window).keydown((e) => {
      const keysMap = {
        "=": {
          id: null,
        },
        1: {
          id: "khronos",
        },
        2: {
          id: "maestro",
        },
        3: {
          id: "concerto",
        },
        4: {
          id: "occam",
        },
        5: {
          id: "hercules",
        },
        6: {
          id: "compass",
        },
        7: {
          id: "shoto",
        },
        8: {
          id: "evergreen",
        },
        9: {
          id: "tsar",
        },
        0: {
          id: "aquilon",
        },
        "-": {
          id: "hydra",
        }
      }

      // Trigger building/kpis change using arrows
      if (this.currentScreen === 3) {
        if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
          const $buildings = $(".algorithm-selector.active .building-selector-element");
          const $currentBuilding = $buildings.filter(".active");
          const $next = e.key === "ArrowLeft" ? $currentBuilding.prev() : $currentBuilding.next();

          // Go to previous/next building if needed
          if ($next.length) {
            const algorithm = $next.closest(".algorithm-selector").attr("data-algorithm");
            const buildingType = $next.attr("data-building-type");
            this.updateBuildingVisually(algorithm, buildingType);
          }
        } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          const currentValues = $(".algorithm-selector.active .kpi").first().data("values");

          const next = e.key === "ArrowUp" ? this.getNextEntry(currentValues, this.currentKPIKey) : this.getPrevEntry(currentValues, this.currentKPIKey);
          if (next) {
            this.currentKPIKey = next.key;
            this.selectKPIs($(".algorithm-selector.active").attr("data-algorithm"), this.currentKPIKey, true);
          }
        }
      }

      // Trigger algo change
      if (Object.hasOwn(keysMap, e.key)) {
        const entry = keysMap[e.key];

        // Algo not found, go back to idle screen
        if (!entry.id) {
          this.goToScreen(1, () => {
            // Make sure all buildings and algos are hidden before any animation
            gsap.set("#algorithms-selectors-container .algorithm-selector", {autoAlpha: 0});
            gsap.set("#algorithms-selectors-container .building-selector-element", {autoAlpha: 0});
          });
          return;
        }

        if (this.currentScreen !== 3) {
          this.goToScreen(3);
        }

        this.currentAlgorithm = entry.id;
        this.updateBuildingVisually(entry.id);
        this.selectKPIs(entry.id, null, true);
      }
    });
  }

  updateEquipmentsState(equipmentPiece, state) {
    const $currentAlgo = this.$container.find(".algorithm-selector.active");
    const $currentBuilding = $currentAlgo.find(".building-selector-element.active");

    const isCurrentEquipmentPieceActive = $currentBuilding.find(`.equipment-piece-state[data-equipment-id=${equipmentPiece}][data-state=${state}]`).hasClass("active")
    if (isCurrentEquipmentPieceActive) {
      // Prevent next steps if current equipment is already active
      return;
    }

    const $currentEquipmentState = $currentBuilding.find(`.equipment-piece-state[data-equipment-id=${equipmentPiece}].active`)
    if ($currentEquipmentState.length > 0) {
      gsap.to($currentEquipmentState, {
        autoAlpha: 0,
        onComplete: () => $currentEquipmentState.removeClass("active")
      });
    }

    const $newEquipmentState = $currentBuilding.find(`.equipment-piece-state[data-equipment-id=${equipmentPiece}][data-state=${state}]`)
    if ($newEquipmentState.length > 0) {
      gsap.killTweensOf($newEquipmentState)
      gsap.to($newEquipmentState, {
        autoAlpha: 1,
        onStart: () => $newEquipmentState.addClass("active"),
        onComplete: () => $newEquipmentState.addClass("active"),
      });
    }
  }

  onScreenBridgeMessage(messageString) {
    super.onScreenBridgeMessage(messageString);

    if (messageString.startsWith("gotoScreen ")) {
      // Make sure we don't go above max number of screens we have
      // For now, the screens with buildings and kpi is 3 so we can limit to that

      const targetScreen = parseInt(messageString.replace("gotoScreen ", ""));
      let screenId = Math.min(this.screensCount, targetScreen);

      // We'll add a "started" flag based on the screen the main app wants us to go
      // Main app resets/go to screen saver or gotoscreen 1 => means it is on the screensaver and waiting to start, we can hide the texts
      // Main app is at a screen more than 1 => means the app is active, we can then show the full UI
      this.started = screenId !== 1;
      this.updateStartStatus();

      // We'll also skip screen 1 and replace it with screen 2,
      // It is then impossible to go to screen 1, which is wanted
      screenId = Math.max(2, screenId);

      if (this.targetScreen !== screenId) this.goToScreen(screenId);
    }

    if (messageString.startsWith("buildingChange")) {
      const messageData = messageString.replace("buildingChange ", "");
      const algo = messageData.split("|")[0];
      const building = messageData.split("|")[1];

      this.updateBuildingVisually(algo, building);
    }

    if (messageString.startsWith("kpiChange")) {
      const messageData = messageString.replace("kpiChange ", "");
      const algo = messageData.split("|")[0];
      const kpiKey = messageData.split("|")[1];
      this.selectKPIs(algo, kpiKey);
    }

    if (messageString.startsWith("updateEquipmentsState")) {
      const messageData = messageString.replace("updateEquipmentsState ", "").split(" ");
      if (messageData.length >= 2) {
        const equipmentPiece = messageData[0];
        const state = messageData[1];
        this.updateEquipmentsState(equipmentPiece, state);
      }
    }
  }

  initScreen(screenId) {
    // console.log("Init screen", screenId);
  }

  updateStartStatus() {
    // console.log("updateStartStatus", this.started);
    $("body").toggleClass("started", this.started);
  }

  goToScreen(screenId, callback = null, skipLockScreenCheck = false, options = {}) {

    // Get screen id without the algorith suffix
    const currentScreenNoSuffix = parseInt(this.currentScreen.toString().split("-")[0]);
    const targetScreenNoSuffix = parseInt(screenId.toString().split("-")[0]);

    // Screen skip in/out transition, if currentTransitionString is listed in exceptions array
    const currentTransitionString = `${currentScreenNoSuffix}->${targetScreenNoSuffix}`;
    if (["2->3"].includes(currentTransitionString)) {
      options.skipOut = true;
      options.skipIn = true;
    }

    // Execute Transition
    this.screenTransition.transition(this.currentScreen, screenId, callback,
      (combinedCallback) => {
        super.goToScreen(screenId, combinedCallback, skipLockScreenCheck, options)
      }, {...options}
    );
  }

  initScreenSaver() {
    // Empty function to bypass the screen saver function
    // This will prevent triggering the Are you still there popup and go back to start
  }

  /**
   * Reset function, used to go back clean version of the game
   * @param screenId
   */
  reset(screenId) {
    // Reset redirect timeout
    if (this.redirectTimeout !== false) {
      clearTimeout(this.redirectTimeout);
      this.redirectTimeout = false;
    }

    // Reset variable and timeline
    this.killTimeline(this.currentTimeline);

    // Reset selection state and stop autoplay
    this.currentAlgorithm = null;
    this.currentBuilding = null;
    this.currentKPIKey = null;
    this.currentKPIsModifiers = [];
    this.pauseSlider();

    const resetCallback = () => {
      // Things that reset after goToScreen
      $(".algorithm-selector, .building-selector-element").removeClass("active");
      gsap.set("#algorithms-selectors-container .algorithm-selector", {autoAlpha: 0});
      gsap.set("#algorithms-selectors-container .building-selector-element", {autoAlpha: 0});
    }

    // Super
    return super.reset(screenId, resetCallback);
  }

  // Temporary functions to get the next property of an object.
  // Used to test with keyboard
  getNextEntry(obj, currentKey) {
    const keys = Object.keys(obj);
    const nextKey = keys[keys.indexOf(currentKey) + 1];
    return nextKey ? {key: nextKey, value: obj[nextKey]} : null;
  }

  getPrevEntry(obj, currentKey) {
    const keys = Object.keys(obj);
    const prevKey = keys[keys.indexOf(currentKey) - 1];
    return prevKey ? {key: prevKey, value: obj[prevKey]} : null;
  }

  getFirstEntry(obj) {
    const firstKey = Object.keys(obj)[0];
    return firstKey ? {key: firstKey, value: obj[firstKey]} : null;
  }
}
